const DB_NAME = "direct-write-store";
const STORE_NAME = "handles";
const DB_VERSION = 1;
const HANDLE_KEY = "default";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
}

export async function saveDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(handle, HANDLE_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadDirectoryHandle(): Promise<FileSystemDirectoryHandle | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(HANDLE_KEY);
    req.onsuccess = () => resolve((req.result as FileSystemDirectoryHandle) || null);
    req.onerror = () => reject(req.error);
  });
}

export async function clearDirectoryHandle(): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(HANDLE_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export type PermissionCheckResult = {
  state: PermissionState;
  queryState?: PermissionState;
  requestState?: PermissionState;
  errorMessage?: string;
};

export async function ensureWritePermission(handle: FileSystemDirectoryHandle): Promise<PermissionCheckResult> {
  const result: PermissionCheckResult = { state: "prompt" };
  const query = (handle as any).queryPermission as (opts?: FileSystemPermissionDescriptor) => Promise<PermissionState> | undefined;
  const request = (handle as any).requestPermission as (opts?: FileSystemPermissionDescriptor) => Promise<PermissionState> | undefined;

  if (typeof query === "function") {
    try {
      const current = await query.call(handle, { mode: "readwrite" });
      result.queryState = current;
      result.state = current ?? result.state;
      if (current === "granted") {
        console.info("[DirectWrite] Permission check", result);
        return result;
      }
    } catch (err: any) {
      result.errorMessage = `queryPermission: ${err?.message || String(err)}`;
    }
  }

  if (typeof request === "function") {
    try {
      const res = await request.call(handle, { mode: "readwrite" });
      result.requestState = res;
      result.state = res ?? result.state;
      console.info("[DirectWrite] Permission check", result);
      return result;
    } catch (err: any) {
      const name = err?.name;
      result.errorMessage = err?.message || String(err);
      // If requestPermission fails without explicit denial (e.g. missing user activation), keep a best-effort state.
      const fallbackState = result.queryState === "denied" ? "denied" : (result.queryState ?? "prompt");
      if (name === "NotAllowedError" || name === "SecurityError" || name === "TypeError") {
        result.state = fallbackState;
      } else {
        result.state = fallbackState;
      }
      console.info("[DirectWrite] Permission check (request failed)", result);
      return result;
    }
  }

  // If APIs are missing, assume prompt (best effort)
  console.info("[DirectWrite] Permission check (fallback)", result);
  return result;
}

export async function requestWritePermission(handle: FileSystemDirectoryHandle): Promise<PermissionCheckResult> {
  const result: PermissionCheckResult = { state: "prompt" };
  const request = (handle as any).requestPermission as (opts?: FileSystemPermissionDescriptor) => Promise<PermissionState> | undefined;

  if (typeof request === "function") {
    try {
      const res = await request.call(handle, { mode: "readwrite" });
      result.requestState = res;
      result.state = res ?? result.state;
      console.info("[DirectWrite] Permission request", result);
      return result;
    } catch (err: any) {
      result.errorMessage = err?.message || String(err);
      console.info("[DirectWrite] Permission request failed", result);
      return result;
    }
  }

  return ensureWritePermission(handle);
}
